import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { AdContextValue, AdProviderConfig } from "./types";

const AdContext = createContext<AdContextValue | null>(null);

const ADSENSE_SCRIPT_ID = "google-adsense-script";

type AdProviderProps = {
  children: ReactNode;
  config: AdProviderConfig;
};

/**
 * 広告プロバイダー
 * AdSenseスクリプトを読み込み、広告コンテキストを提供
 */
export function AdProvider({ children, config }: AdProviderProps) {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // 広告が無効の場合はスクリプトを読み込まない
    if (!config.enabled) {
      return;
    }

    // 既にスクリプトが読み込まれている場合
    if (document.getElementById(ADSENSE_SCRIPT_ID)) {
      setIsLoaded(true);
      return;
    }

    // AdSenseスクリプトを動的に読み込み
    const script = document.createElement("script");
    script.id = ADSENSE_SCRIPT_ID;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.publisherId}`;
    script.async = true;
    script.crossOrigin = "anonymous";

    script.onload = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      console.warn("[Ad] Failed to load AdSense script");
    };

    document.head.appendChild(script);

    return () => {
      // クリーンアップは行わない（SPAでの再読み込みを避ける）
    };
  }, [config.enabled, config.publisherId]);

  return <AdContext.Provider value={{ config, isLoaded }}>{children}</AdContext.Provider>;
}

/**
 * 広告コンテキストを取得するフック
 */
export function useAdContext(): AdContextValue | null {
  return useContext(AdContext);
}
